// src/store/jobSlice.js
import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import {
  fetchGetAllJobs,
  fetchGetJobById,
  fetchGetSavedJobs,
} from "../api/jobsAPI";
import { setError, clearError } from "./uiSlice";
import { fetchCreateJob, fetchSaveJobs, fetchUnSaveJobs } from "../api/jobsAPI";
import { logoutUser } from "./authSlice";

// ✅ Get all jobs from jobs.json
// ✅ Get all jobs from Appwrite (optionally filtered by Type)
export const getAllJobs = createAsyncThunk(
  "jobs/getAllJobs",
  async (type = null, { getState, rejectWithValue, dispatch }) => {
    const { job } = getState();
    const isStale = job.jobsStale[type || "all"];

    // 🔹 If not stale, return cached jobs
    if (!isStale) {
      return { success: true, jobs: job.jobs, fromCache: true, type };
    }

    try {
      const result = await fetchGetAllJobs(type);
      if (!result.success) {
        dispatch(setError(result.message || "Fetching jobs failed"));
        return rejectWithValue(result.message);
      }
      dispatch(clearError());
      return { ...result, fromCache: false, type };
    } catch (err) {
      dispatch(setError("Fetching jobs failed."));
      return rejectWithValue(err.message);
    }
  }
);

// ✅ Get single job from jobs.json
export const GetJobById = createAsyncThunk(
  "job/getJobById",
  async (jobId, { getState, rejectWithValue, dispatch }) => {
    try {
      const { jobs } = getState().job;
      const cachedJob = jobs.find((j) => j.$id === jobId);

      // 🔹 Return cached job if found
      if (cachedJob) {
        return { success: true, data: cachedJob, fromCache: true };
      }

      // 🔹 Otherwise fetch from API
      const response = await fetchGetJobById(jobId);
      if (!response.success) {
        dispatch(setError(response.message || "Job not found"));
        return rejectWithValue(response.message);
      }
      dispatch(clearError());
      return { ...response, fromCache: false };
    } catch (err) {
      dispatch(setError("Fetching job failed."));
      return rejectWithValue(err.message);
    }
  }
);

// ✅ Client-side search & filtering (frontend only)
export const searchJobs = createAsyncThunk(
  "job/searchJobs",
  async ({ query = "", countries = [], cities = [] }, { getState }) => {
    const { jobs } = getState().job; // all jobs already in Redux
    let filtered = jobs;

    // 🌍 Country filter
    if (countries.length > 0) {
      filtered = filtered.filter((j) => countries.includes(j.Country));
    }

    // 🏙️ City filter
    if (cities.length > 0) {
      filtered = filtered.filter((j) => cities.includes(j.City));
    }

    // 🔎 Search filter (multi-field)
    if (query) {
      const lowerQuery = String(query).toLowerCase();
      const toText = (v) => String(v ?? "").toLowerCase();

      filtered = filtered.filter((j) =>
        [
          j.Title,
          j.Company,
          j.City,
          j.Country,
          j.Salary,
          j.WorkType,
          j.Experience,
          j.Batch,
          j.Degree,
          j.Skills,
          j.Responsibilities,
          j.CompanyDescription,
          j.Description,
          j.Type,
        ].some((field) => toText(field).includes(lowerQuery))
      );
    }

    return filtered;
  }
);

// ✅ Create job thunk
export const CreateJob = createAsyncThunk(
  "job/createJob",
  async (payload, { rejectWithValue, dispatch }) => {
    try {
      const result = await fetchCreateJob(payload);
      if (!result.success) {
        dispatch(setError(result.message || "Failed to create job"));
        return rejectWithValue(result.message);
      }
      dispatch(clearError());
      return result;
    } catch (err) {
      dispatch(setError("Failed to create job."));
      return rejectWithValue(err.message);
    }
  }
);

export const saveJobs = createAsyncThunk(
  "job/saveJobs",
  async ({ UserId, JobId }) => {
    try {
      const res = await fetchSaveJobs({ UserId, JobId });
      return res;
    } catch (err) {
      console.error(err);
    }
  }
);

export const unSaveJobs = createAsyncThunk(
  "job/unSaveJobs",
  async ({ UserId, JobId }) => {
    try {
      const res = await fetchUnSaveJobs({ UserId, JobId });
      return res;
    } catch (err) {
      console.error(err);
    }
  }
);

export const SavedJobs = createAsyncThunk(
  "job/savedJobs",
  async ({ UserId }) => {
    try {
      const res = await fetchGetSavedJobs({ UserId });
      return res;
    } catch (err) {
      console.error(err);
    }
  }
);

const jobSlice = createSlice({
  name: "job",
  initialState: {
    jobs: [],
    filteredJobs: null,
    loading: false,
    countries: [],
    cities: [],
    data: {},
    status: false,
    savedJobs: [],
    savedJobsStale: true,
    jobsStale: { Job: true, Internship: true },
  },
  reducers: {
    clearFilteredJobs(state) {
      state.filteredJobs = null;
    },
    markSavedJobsStale(state) {
      state.savedJobsStale = true; // optional: manually mark stale
    },
    clearJobError: (state) => {
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    builder
      // ✅ Get all jobs
      .addCase(getAllJobs.pending, (state) => {
        state.loading = true;
      })
      .addCase(getAllJobs.fulfilled, (state, action) => {
        state.loading = false;
        const type = action.payload.type || "all";

        if (!action.payload.fromCache) {
          if (type === "all") {
            state.jobs = action.payload.jobs;
          } else {
            state.jobs = state.jobs.filter((j) => j.Type !== type);
            state.jobs = [...state.jobs, ...action.payload.jobs];
          }
          state.jobsStale[type] = false;
        }
        state.countries = [
          ...new Set(state.jobs.map((j) => j.Country).filter(Boolean)),
        ];
        state.cities = [
          ...new Set(state.jobs.map((j) => j.City).filter(Boolean)),
        ];
      })

      .addCase(getAllJobs.rejected, (state, action) => {
        state.loading = false;
        state.jobsStale[action.meta.arg || "all"] = true; // keep it stale
      })
      // ✅ Get single job
      .addCase(GetJobById.fulfilled, (state, action) => {
        state.loading = false;
        state.data = action.payload.data || {};
        state.status = action.payload.success;

        // 🔹 If it came from API (not cache), update jobs list
        if (!action.payload.fromCache && action.payload.data) {
          const idx = state.jobs.findIndex(
            (j) => j.$id === action.payload.data.$id
          );
          if (idx === -1) {
            state.jobs.push(action.payload.data);
          } else {
            state.jobs[idx] = action.payload.data;
          }
        }
      })

      // ✅ Search jobs
      .addCase(searchJobs.fulfilled, (state, action) => {
        state.loading = false;
        state.filteredJobs = action.payload;
      })
      .addCase(CreateJob.pending, (state) => {
        state.loading = true;
      })
      .addCase(CreateJob.fulfilled, (state, action) => {
        state.loading = false;
        if (action.payload?.data) {
          state.jobs.unshift(action.payload.data); // add new job to state
          state.jobsStale[action.payload.data.Type] = true;
        }
      })
      .addCase(CreateJob.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error?.message || "Failed to create job";
      })
      // .addCase(saveJobs.pending, (state) => {
      //   state.loading = true;
      // })
      .addCase(saveJobs.fulfilled, (state, action) => {
        if (
          action.payload?.jobId &&
          !state.savedJobs.includes(action.payload.jobId)
        ) {
          state.savedJobs.push(action.payload.jobId);
          state.savedJobsStale = false; // data is fresh now
        }
      })
      .addCase(saveJobs.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message;
      })
      // .addCase(unSaveJobs.pending, (state) => {
      //   state.loading = true;
      // })
      .addCase(unSaveJobs.fulfilled, (state, action) => {
        if (action.payload?.jobId) {
          state.savedJobs = state.savedJobs.filter(
            (id) => id !== action.payload.jobId
          );
          state.savedJobsStale = false; // data is fresh now
        }
      })
      .addCase(unSaveJobs.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message;
      })
      .addCase(SavedJobs.pending, (state) => {
        state.loading = true;
      })
      .addCase(SavedJobs.fulfilled, (state, action) => {
        state.loading = false;
        state.savedJobs = Array.isArray(action.payload) ? action.payload : [];
        state.savedJobsStale = false;
      })
      .addCase(SavedJobs.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message;
      })
      .addCase(logoutUser.fulfilled, (state) => {
        state.savedJobs = []; // clear saved jobs
        state.savedJobsStale = true; // force refresh on next login
      });
  },
});

export const { clearFilteredJobs, markSavedJobsStale, clearJobError } =
  jobSlice.actions;
export default jobSlice.reducer;
